import React, { useCallback, useMemo, useState } from 'react';

import { Box, Button, Chip, Container, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import {
  DPIA_IMPACT_OPTIONS,
  DPIA_LIKELIHOOD_OPTIONS,
  DPIA_MEASURE_STATUS_OPTIONS,
  DPIA_PRIOR_CONSULTATION_OPTIONS,
  DPIA_RESIDUAL_RISK_OPTIONS,
} from 'config/api/dpias/dpias.constants';
import { calculateMockRiskLevel } from 'config/api/dpias/dpias.helpers';
import type { DpiaRisk } from 'config/api/dpias/dpias.types';
import usePageTitle from 'hooks/usePageTitle';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import { getDpiaDecisionLabel } from '../_shared/apaDpiaStatus.helpers';
import { canAccessOwnerDpiaEditorPage } from '../_shared/assetRopaReviewAccess';
import DpiaHistoryList from '../_shared/DpiaHistoryList';
import DpiaReassessDialog from '../_shared/DpiaReassessDialog';
import { formatDpiaSidePanelList, formatDpiaSidePanelText } from '../_shared/dpiaSidePanelDisplay.helpers';
import DpiaTriggerReadOnly from '../_shared/DpiaTriggerReadOnly';
import QueryUnavailableState from '../_shared/QueryUnavailableState';
import useDpiaHistory from '../_shared/useDpiaHistory';
import useDpiaReassess from '../_shared/useDpiaReassess';
import DpiaReviewHistoryList from '../AdminDpiaReviewPage/_components/DpiaReviewHistoryList';
import { buildDpiaNeedAssessmentLocation } from '../DpiaNeedAssessmentPage/dpiaNeedAssessment.helpers';
import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import {
  buildCompletenessItems,
  createEmptyMeasure,
  createEmptyRisk,
  getNextRiskSortOrder,
  hasValidationErrors,
  isOwnerDpiaEditable,
  validateDpiaForSubmit,
} from './ownerDpiaEditor.helpers';
import useOwnerDpiaEditor from './useOwnerDpiaEditor';

type RouteParams = {
  dpiaId: string;
};

type LocationState = {
  returnPath?: string;
};

const OwnerDpiaEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { dpiaId } = useParams<RouteParams>();
  const { enqueueSnackbar } = useSnackbar();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canAccess = canAccessOwnerDpiaEditorPage(userData);
  const messages = processing_activities_messages.asset_ropa.owner_dpia;
  const [expandedRiskIds, setExpandedRiskIds] = useState<number[]>([]);

  usePageTitle(t(messages.page_title));

  const returnPath = location.state?.returnPath ?? PATHS.PROCESSING_ACTIVITIES_ASSET_ROPA;

  const navigateBack = useCallback(() => {
    history.push(returnPath);
  }, [history, returnPath]);

  const validationMessages = useMemo(
    () => ({
      required: t(validation_messages.required),
      purposeScope: t(messages.validation.purpose_scope),
      riskTitle: t(messages.validation.risk_title),
      riskDescription: t(messages.validation.risk_description),
      riskAffectedRights: t(messages.validation.risk_affected_rights),
      riskLikelihood: t(messages.validation.risk_likelihood),
      riskImpact: t(messages.validation.risk_impact),
      riskMeasures: t(messages.validation.risk_measures),
      measureTitle: t(messages.validation.measure_title),
      measureDescription: t(messages.validation.measure_description),
      measureResponsible: t(messages.validation.measure_responsible),
      measureStatus: t(messages.validation.measure_status),
      clarificationResponse: t(messages.validation.clarification_response),
      residualRisk: t(messages.validation.residual_risk),
      priorConsultation: t(messages.validation.prior_consultation),
    }),
    [messages, t],
  );

  const {
    bannerMeta,
    context,
    detail,
    measureResponsibleDefault,
    formValues,
    handleNavigation,
    handleSave,
    handleSubmit,
    isError,
    isLoading,
    isSaving,
    isSubmitting,
    isUnavailable,
    unavailableStatus,
    NavigationPrompt,
    patchFormValues,
    refetch,
    setFormValues,
    setIsDirty,
    setValidationErrors,
    sourceTrigger,
    validationErrors,
  } = useOwnerDpiaEditor({
    dpiaId: Number(dpiaId),
    onConflict: () => {
      enqueueSnackbar(t(messages.toasts.conflict), { variant: 'warning' });
    },
    onNavigate: navigateBack,
    onSaveSuccess: closeAfterSave => {
      enqueueSnackbar(t(messages.toasts.saved), { variant: 'success' });
      if (closeAfterSave) navigateBack();
    },
    onSubmitSuccess: () => {
      enqueueSnackbar(t(messages.toasts.submitted), { variant: 'success' });
      navigateBack();
    },
    organizationId,
  });

  const editable = detail ? isOwnerDpiaEditable(detail.status) && detail.canEdit : false;
  const hasClarificationThread = Boolean(detail?.clarificationHistory.length);
  const showClarificationSection =
    hasClarificationThread || Boolean(detail?.administratorClarificationComment) || detail?.status === 'clarification_requested';
  const canReassess = detail?.status === 'decision_made';
  const completenessItems = formValues && detail ? buildCompletenessItems(formValues, detail.status) : [];
  const [reassessOpen, setReassessOpen] = useState(false);
  const reassessMutation = useDpiaReassess(organizationId);
  const dpiaHistoryQuery = useDpiaHistory({
    apaId: detail?.assetProcessingActivityId,
    enabled: Boolean(detail?.assetProcessingActivityId),
    organizationId,
  });

  const updateRisks = useCallback(
    (updater: (risks: DpiaRisk[]) => DpiaRisk[]) => {
      setFormValues(current => {
        if (!current) return current;
        const nextRisks = updater(current.risks);
        setIsDirty(true);
        setValidationErrors({});
        return { ...current, risks: nextRisks };
      });
    },
    [setFormValues, setIsDirty, setValidationErrors],
  );

  const handleAddRisk = useCallback(() => {
    if (!detail || !formValues) {
      return;
    }

    const nextRiskId = -Math.round(Math.random() * 1000000);
    const responsible = measureResponsibleDefault ?? {
      responsibleMembershipId: detail.responsibleMembershipId,
      responsibleName: detail.responsibleName,
    };
    const risk = createEmptyRisk(
      nextRiskId,
      getNextRiskSortOrder(formValues.risks),
      responsible.responsibleMembershipId,
      responsible.responsibleName,
    );

    updateRisks(risks => [...risks, risk]);
    setExpandedRiskIds(current => [...current, risk.id]);
  }, [detail, formValues, measureResponsibleDefault, updateRisks]);

  const handleSubmitClick = useCallback(async () => {
    if (!formValues || !detail) return;

    const errors = validateDpiaForSubmit(formValues, detail.status, validationMessages);
    setValidationErrors(errors);

    if (hasValidationErrors(errors)) {
      enqueueSnackbar(t(messages.validation.submit_blocked), { variant: 'warning' });
      return;
    }

    try {
      await handleSubmit();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        enqueueSnackbar(t(messages.toasts.conflict), { variant: 'warning' });
      } else {
        enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
      }
    }
  }, [detail, enqueueSnackbar, formValues, handleSubmit, messages, setValidationErrors, t, validationMessages]);

  const handleSaveClick = useCallback(
    async (closeAfterSave: boolean) => {
      try {
        await handleSave(closeAfterSave);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          enqueueSnackbar(t(messages.toasts.conflict), { variant: 'warning' });
        } else {
          enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
        }
      }
    },
    [enqueueSnackbar, handleSave, messages, t],
  );

  const handleOpenHistoryItem = useCallback(
    (item: { id: number }) => {
      if (String(item.id) === dpiaId) {
        return;
      }

      history.push(generatePath(PATHS.PROCESSING_ACTIVITIES_OWNER_DPIA, { dpiaId: String(item.id) }), {
        returnPath,
      });
    },
    [dpiaId, history, returnPath],
  );

  const handleReassessConfirm = useCallback(
    async (reason: string) => {
      if (!detail) return;

      try {
        const currentId = await reassessMutation.mutateAsync({
          apaId: detail.assetProcessingActivityId,
          reason,
        });
        enqueueSnackbar(t(messages.toasts.reassessed), { variant: 'success' });
        setReassessOpen(false);
        history.push(generatePath(PATHS.PROCESSING_ACTIVITIES_OWNER_DPIA, { dpiaId: String(currentId) }), {
          returnPath,
        });
      } catch {
        enqueueSnackbar(t(messages.toasts.reassess_failed), { variant: 'error' });
      }
    },
    [detail, enqueueSnackbar, history, messages.toasts, reassessMutation, returnPath, t],
  );

  let content: React.ReactNode;

  if (!canAccess) {
    content = (
      <Typography color='textSecondary' variant='body2'>
        {t(processing_activities_messages.asset_ropa.forbidden_body)}
      </Typography>
    );
  } else if (isUnavailable) {
    content = (
      <QueryUnavailableState
        backLabel={t(general_messages.back)}
        message={
          unavailableStatus === 403 ? t(processing_activities_messages.asset_ropa.owner_forbidden_body) : t(messages.errors.not_found)
        }
        onBack={navigateBack}
      />
    );
  } else if (isError) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='error' variant='body2'>
          {t(processing_activities_messages.asset_ropa.errors.load_failed)}
        </Typography>
        <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>
      </Box>
    );
  } else if (isLoading || !formValues || !detail || !context || !bannerMeta) {
    content = <Loader inner />;
  } else {
    content = (
      <>
        <Box className={styles.reviewPanel}>
          <div className={styles.reviewMeta}>
            <span>
              <strong>{t(messages.status)}:</strong>{' '}
              <span className={`${styles.statusBadge} ${styles.statusBadgeWarn}`}>{t(messages.statuses[detail.status])}</span>
            </span>
            <span>
              <strong>{t(messages.responsible_owner)}:</strong> {formatDpiaSidePanelText(bannerMeta.responsibleOwner)}
            </span>
            <span>
              <strong>{t(messages.initiated)}:</strong> {formatDpiaSidePanelText(bannerMeta.initiatedDate)}
            </span>
            {bannerMeta.hasSourceRequest && (
              <span>
                <strong>{t(messages.source)}:</strong> {t(messages.source_request)}
              </span>
            )}
          </div>
        </Box>

        <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
          {t(messages.truth_note)}
        </Typography>

        <div className={styles.twoColumnLayout}>
          <aside>
            <Box className={styles.sidePanel}>
              <div className={styles.reviewEyebrow}>{t(messages.side_panel.eyebrow)}</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.side_panel.title)}
              </Typography>
              <div className={styles.fieldList}>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.asset)}</div>
                  <div>{formatDpiaSidePanelText(context.assetName)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.processing_activity)}</div>
                  <div>{formatDpiaSidePanelText(context.assetProcessingActivityName)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.asset_type)}</div>
                  <div>{formatDpiaSidePanelText(context.assetTypeName)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.owner)}</div>
                  <div>{formatDpiaSidePanelText(context.operationalOwnerName)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.data_subjects)}</div>
                  <div>{formatDpiaSidePanelList(context.dataSubjectCategories)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.personal_data)}</div>
                  <div>{formatDpiaSidePanelList(context.personalDataCategories)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.countries)}</div>
                  <div>{formatDpiaSidePanelList(context.countryHandling)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.minimum_security)}</div>
                  <div>{formatDpiaSidePanelText(context.minimumSecurityAssessment)}</div>
                </div>
              </div>

              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(messages.side_panel.purpose_scope)}
              </Typography>
              {context.availablePurposes.map(purpose => (
                <Box key={purpose.id} className={styles.purposePanel}>
                  <Typography variant='body2'>
                    <strong>{purpose.name}</strong>
                  </Typography>
                  <Typography color='textSecondary' variant='caption'>
                    {purpose.legalBasis}
                  </Typography>
                  <Typography variant='body2'>{purpose.applicationDescription}</Typography>
                </Box>
              ))}

              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(messages.side_panel.source_request)}
              </Typography>
              <Typography variant='body2'>
                <strong>{t(messages.side_panel.recommendation)}:</strong> {formatDpiaSidePanelText(context.sourceRequest.recommendation)}
              </Typography>
              <Typography variant='body2'>
                <strong>{t(messages.side_panel.decision)}:</strong> {formatDpiaSidePanelText(context.sourceRequest.decision)}
              </Typography>
              <Typography color='textSecondary' variant='body2'>
                {formatDpiaSidePanelText(context.sourceRequest.decisionComment)}
              </Typography>
              {sourceTrigger && <DpiaTriggerReadOnly trigger={sourceTrigger} />}
              {bannerMeta.sourceDpiaRequestId && (
                <TextButton
                  onClick={() =>
                    history.push(
                      buildDpiaNeedAssessmentLocation({
                        apaId: detail.assetProcessingActivityId,
                        requestId: bannerMeta.sourceDpiaRequestId ?? undefined,
                        returnPath: history.location.pathname,
                      }),
                    )
                  }
                >
                  {t(messages.open_source_request)}
                </TextButton>
              )}

              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(messages.side_panel.history)}
              </Typography>
              {dpiaHistoryQuery.isLoading && <Loader inner />}
              {dpiaHistoryQuery.history && <DpiaHistoryList items={dpiaHistoryQuery.history.items} onOpenItem={handleOpenHistoryItem} />}
            </Box>
          </aside>

          <section className={styles.mainPanel}>
            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 1</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.scope.title)}
              </Typography>
              <TextField
                disabled={!editable}
                error={Boolean(validationErrors.plannedProcessingDescription)}
                fullWidth
                helperText={validationErrors.plannedProcessingDescription}
                label={t(messages.sections.scope.planned_processing)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ plannedProcessingDescription: event.target.value })}
                rows={4}
                value={formValues.plannedProcessingDescription}
                variant='outlined'
              />
              <TextField
                disabled={!editable}
                error={Boolean(validationErrors.assessmentScope)}
                fullWidth
                helperText={validationErrors.assessmentScope}
                label={t(messages.sections.scope.assessment_scope)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ assessmentScope: event.target.value })}
                rows={3}
                value={formValues.assessmentScope}
                variant='outlined'
              />
              <TextField
                disabled={!editable}
                error={Boolean(validationErrors.projectOrChangeContext)}
                fullWidth
                helperText={validationErrors.projectOrChangeContext}
                label={t(messages.sections.scope.project_context)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ projectOrChangeContext: event.target.value })}
                rows={3}
                value={formValues.projectOrChangeContext}
                variant='outlined'
              />
              <Typography gutterBottom variant='body2'>
                {t(messages.sections.scope.purposes)}
              </Typography>
              <div className={styles.reviewActions}>
                {context.availablePurposes.map(purpose => {
                  const selected = formValues.selectedPurposeIds.includes(purpose.id);
                  return (
                    <Chip
                      key={purpose.id}
                      color={selected ? 'primary' : 'default'}
                      disabled={!editable}
                      label={purpose.name}
                      onClick={() => {
                        if (!editable) return;
                        patchFormValues({
                          selectedPurposeIds: selected
                            ? formValues.selectedPurposeIds.filter(id => id !== purpose.id)
                            : [...formValues.selectedPurposeIds, purpose.id],
                        });
                      }}
                    />
                  );
                })}
              </div>
              {validationErrors.selectedPurposeIds && (
                <Typography color='error' variant='caption'>
                  {validationErrors.selectedPurposeIds}
                </Typography>
              )}
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 2</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.necessity.title)}
              </Typography>
              {(
                ['necessityAssessment', 'proportionalityAssessment', 'dataMinimisationAssessment', 'retentionAndAccessAssessment'] as const
              ).map(field => (
                <TextField
                  key={field}
                  disabled={!editable}
                  error={Boolean(validationErrors[field])}
                  fullWidth
                  helperText={validationErrors[field]}
                  label={t(messages.sections.necessity[field])}
                  margin='normal'
                  multiline
                  onChange={event => patchFormValues({ [field]: event.target.value })}
                  rows={3}
                  value={formValues[field]}
                  variant='outlined'
                />
              ))}
            </Box>

            <Box className={styles.apaSection}>
              {formValues.risks.length === 0 ? (
                <div className={styles.reviewPanelHeader}>
                  <div>
                    <div className={styles.reviewEyebrow}>SECTION 3</div>
                    <Typography variant='h3'>{t(messages.sections.risks.title)}</Typography>
                    <Typography color='textSecondary' variant='body2'>
                      {t(messages.sections.risks.help)}
                    </Typography>
                  </div>
                  {editable && (
                    <Button onClick={handleAddRisk} variant='outlined'>
                      {t(messages.sections.risks.add_risk)}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className={styles.reviewEyebrow}>SECTION 3</div>
                  <Typography variant='h3'>{t(messages.sections.risks.title)}</Typography>
                  <Typography color='textSecondary' gutterBottom variant='body2'>
                    {t(messages.sections.risks.help)}
                  </Typography>
                </>
              )}
              {validationErrors.risks && (
                <Typography color='error' gutterBottom variant='body2'>
                  {validationErrors.risks}
                </Typography>
              )}
              {formValues.risks.map((risk, riskIndex) => {
                const expanded = expandedRiskIds.includes(risk.id) || riskIndex === 0;
                const riskLevel =
                  calculateMockRiskLevel(risk.likelihood, risk.impact) || risk.riskLevel || t(messages.sections.risks.not_assessed);

                return (
                  <Box key={risk.id} className={styles.purposePanel}>
                    <div className={styles.reviewPanelHeader}>
                      <div>
                        <Typography variant='body1'>
                          <strong>{risk.title || t(messages.sections.risks.untitled)}</strong>
                        </Typography>
                        <Typography color='textSecondary' variant='caption'>
                          {t(messages.sections.risks.likelihood)}: {risk.likelihood || '—'} · {t(messages.sections.risks.impact)}:{' '}
                          {risk.impact || '—'} · {riskLevel}
                        </Typography>
                      </div>
                      <div>
                        <Button
                          onClick={() =>
                            setExpandedRiskIds(current => (expanded ? current.filter(id => id !== risk.id) : [...current, risk.id]))
                          }
                        >
                          {expanded ? t(messages.sections.risks.collapse) : t(messages.sections.risks.expand)}
                        </Button>
                        {editable && (
                          <Button color='secondary' onClick={() => updateRisks(risks => risks.filter(item => item.id !== risk.id))}>
                            {t(general_messages.delete)}
                          </Button>
                        )}
                      </div>
                    </div>
                    {expanded && (
                      <>
                        <TextField
                          disabled={!editable}
                          error={Boolean(validationErrors[`risk.${riskIndex}.title`])}
                          fullWidth
                          helperText={validationErrors[`risk.${riskIndex}.title`]}
                          label={t(messages.sections.risks.risk_title)}
                          margin='normal'
                          onChange={event =>
                            updateRisks(risks => risks.map(item => (item.id === risk.id ? { ...item, title: event.target.value } : item)))
                          }
                          value={risk.title}
                          variant='outlined'
                        />
                        <TextField
                          disabled={!editable}
                          error={Boolean(validationErrors[`risk.${riskIndex}.description`])}
                          fullWidth
                          helperText={validationErrors[`risk.${riskIndex}.description`]}
                          label={t(messages.sections.risks.risk_description)}
                          margin='normal'
                          multiline
                          onChange={event =>
                            updateRisks(risks =>
                              risks.map(item => (item.id === risk.id ? { ...item, description: event.target.value } : item)),
                            )
                          }
                          rows={3}
                          value={risk.description}
                          variant='outlined'
                        />
                        <TextField
                          disabled={!editable}
                          error={Boolean(validationErrors[`risk.${riskIndex}.affectedRightsAndFreedoms`])}
                          fullWidth
                          helperText={validationErrors[`risk.${riskIndex}.affectedRightsAndFreedoms`]}
                          label={t(messages.sections.risks.affected_rights)}
                          margin='normal'
                          multiline
                          onChange={event =>
                            updateRisks(risks =>
                              risks.map(item => (item.id === risk.id ? { ...item, affectedRightsAndFreedoms: event.target.value } : item)),
                            )
                          }
                          rows={3}
                          value={risk.affectedRightsAndFreedoms}
                          variant='outlined'
                        />
                        <div className={styles.filtersRow}>
                          <FormControl disabled={!editable} fullWidth margin='normal' variant='outlined'>
                            <InputLabel>{t(messages.sections.risks.likelihood)}</InputLabel>
                            <Select
                              label={t(messages.sections.risks.likelihood)}
                              onChange={event =>
                                updateRisks(risks =>
                                  risks.map(item =>
                                    item.id === risk.id
                                      ? {
                                          ...item,
                                          likelihood: event.target.value as typeof risk.likelihood,
                                          riskLevel:
                                            calculateMockRiskLevel(event.target.value as typeof risk.likelihood, item.impact) || '',
                                        }
                                      : item,
                                  ),
                                )
                              }
                              value={risk.likelihood}
                            >
                              {DPIA_LIKELIHOOD_OPTIONS.map(option => (
                                <MenuItem key={option} value={option}>
                                  {t(messages.likelihood[option])}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl disabled={!editable} fullWidth margin='normal' variant='outlined'>
                            <InputLabel>{t(messages.sections.risks.impact)}</InputLabel>
                            <Select
                              label={t(messages.sections.risks.impact)}
                              onChange={event =>
                                updateRisks(risks =>
                                  risks.map(item =>
                                    item.id === risk.id
                                      ? {
                                          ...item,
                                          impact: event.target.value as typeof risk.impact,
                                          riskLevel:
                                            calculateMockRiskLevel(item.likelihood, event.target.value as typeof risk.impact) || '',
                                        }
                                      : item,
                                  ),
                                )
                              }
                              value={risk.impact}
                            >
                              {DPIA_IMPACT_OPTIONS.map(option => (
                                <MenuItem key={option} value={option}>
                                  {t(messages.impact[option])}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                        <Typography gutterBottom variant='h4'>
                          {t(messages.sections.risks.measures)}
                        </Typography>
                        {validationErrors[`risk.${riskIndex}.measures`] && (
                          <Typography color='error' variant='caption'>
                            {validationErrors[`risk.${riskIndex}.measures`]}
                          </Typography>
                        )}
                        {risk.measures.map((measure, measureIndex) => (
                          <Box key={measure.id} className={styles.countryRowCard}>
                            <div className={styles.reviewPanelHeader}>
                              <Typography color='textSecondary' variant='caption'>
                                {t(messages.sections.risks.measure_title)}
                              </Typography>
                              {editable && (
                                <Button
                                  color='secondary'
                                  onClick={() =>
                                    updateRisks(risks =>
                                      risks.map(item =>
                                        item.id === risk.id
                                          ? {
                                              ...item,
                                              measures: item.measures.filter(current => current.id !== measure.id),
                                            }
                                          : item,
                                      ),
                                    )
                                  }
                                  size='small'
                                >
                                  {t(general_messages.delete)}
                                </Button>
                              )}
                            </div>
                            <TextField
                              disabled={!editable}
                              error={Boolean(validationErrors[`risk.${riskIndex}.measure.${measureIndex}.title`])}
                              fullWidth
                              helperText={validationErrors[`risk.${riskIndex}.measure.${measureIndex}.title`]}
                              label={t(messages.sections.risks.measure_title)}
                              margin='normal'
                              onChange={event =>
                                updateRisks(risks =>
                                  risks.map(item =>
                                    item.id === risk.id
                                      ? {
                                          ...item,
                                          measures: item.measures.map(current =>
                                            current.id === measure.id ? { ...current, title: event.target.value } : current,
                                          ),
                                        }
                                      : item,
                                  ),
                                )
                              }
                              value={measure.title}
                              variant='outlined'
                            />
                            <TextField
                              disabled={!editable}
                              error={Boolean(validationErrors[`risk.${riskIndex}.measure.${measureIndex}.description`])}
                              fullWidth
                              helperText={validationErrors[`risk.${riskIndex}.measure.${measureIndex}.description`]}
                              label={t(messages.sections.risks.measure_description)}
                              margin='normal'
                              multiline
                              onChange={event =>
                                updateRisks(risks =>
                                  risks.map(item =>
                                    item.id === risk.id
                                      ? {
                                          ...item,
                                          measures: item.measures.map(current =>
                                            current.id === measure.id ? { ...current, description: event.target.value } : current,
                                          ),
                                        }
                                      : item,
                                  ),
                                )
                              }
                              rows={2}
                              value={measure.description}
                              variant='outlined'
                            />
                            <FormControl disabled={!editable} fullWidth margin='normal' variant='outlined'>
                              <InputLabel>{t(messages.sections.risks.measure_status)}</InputLabel>
                              <Select
                                label={t(messages.sections.risks.measure_status)}
                                onChange={event =>
                                  updateRisks(risks =>
                                    risks.map(item =>
                                      item.id === risk.id
                                        ? {
                                            ...item,
                                            measures: item.measures.map(current =>
                                              current.id === measure.id
                                                ? { ...current, status: event.target.value as typeof measure.status }
                                                : current,
                                            ),
                                          }
                                        : item,
                                    ),
                                  )
                                }
                                value={measure.status}
                              >
                                {DPIA_MEASURE_STATUS_OPTIONS.map(option => (
                                  <MenuItem key={option} value={option}>
                                    {t(messages.measure_status[option])}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              disabled={!editable}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              label={t(messages.sections.risks.measure_due_date)}
                              margin='normal'
                              onChange={event =>
                                updateRisks(risks =>
                                  risks.map(item =>
                                    item.id === risk.id
                                      ? {
                                          ...item,
                                          measures: item.measures.map(current =>
                                            current.id === measure.id ? { ...current, dueDate: event.target.value || null } : current,
                                          ),
                                        }
                                      : item,
                                  ),
                                )
                              }
                              type='date'
                              value={measure.dueDate ?? ''}
                              variant='outlined'
                            />
                            <TextField
                              disabled
                              error={Boolean(validationErrors[`risk.${riskIndex}.measure.${measureIndex}.responsible`])}
                              fullWidth
                              helperText={validationErrors[`risk.${riskIndex}.measure.${measureIndex}.responsible`]}
                              label={t(messages.sections.risks.responsible)}
                              margin='normal'
                              value={measure.responsibleName}
                              variant='outlined'
                            />
                          </Box>
                        ))}
                        {editable && (
                          <Button
                            onClick={() => {
                              const nextMeasureId = -Math.round(Math.random() * 1000000);
                              const responsible = measureResponsibleDefault ?? {
                                responsibleMembershipId: detail.responsibleMembershipId,
                                responsibleName: detail.responsibleName,
                              };
                              updateRisks(risks =>
                                risks.map(item =>
                                  item.id === risk.id
                                    ? {
                                        ...item,
                                        measures: [
                                          ...item.measures,
                                          createEmptyMeasure(
                                            nextMeasureId,
                                            risk.id,
                                            responsible.responsibleMembershipId,
                                            responsible.responsibleName,
                                          ),
                                        ],
                                      }
                                    : item,
                                ),
                              );
                            }}
                            variant='text'
                          >
                            {t(messages.sections.risks.add_measure)}
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                );
              })}
              {editable && formValues.risks.length > 0 && (
                <Button onClick={handleAddRisk} variant='outlined'>
                  {t(messages.sections.risks.add_risk)}
                </Button>
              )}
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 4</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.overall.title)}
              </Typography>
              <TextField
                disabled={!editable}
                error={Boolean(validationErrors.overallAssessment)}
                fullWidth
                helperText={validationErrors.overallAssessment}
                label={t(messages.sections.overall.overall_assessment)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ overallAssessment: event.target.value })}
                rows={4}
                value={formValues.overallAssessment}
                variant='outlined'
              />
              <div className={styles.filtersRow}>
                <FormControl
                  disabled={!editable}
                  error={Boolean(validationErrors.residualRiskLevel)}
                  fullWidth
                  margin='normal'
                  variant='outlined'
                >
                  <InputLabel>{t(messages.sections.overall.residual_risk)}</InputLabel>
                  <Select
                    label={t(messages.sections.overall.residual_risk)}
                    onChange={event => patchFormValues({ residualRiskLevel: event.target.value as typeof formValues.residualRiskLevel })}
                    value={formValues.residualRiskLevel}
                  >
                    {DPIA_RESIDUAL_RISK_OPTIONS.map(option => (
                      <MenuItem key={option} value={option}>
                        {t(messages.residual_risk[option])}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.residualRiskLevel && (
                    <Typography color='error' variant='caption'>
                      {validationErrors.residualRiskLevel}
                    </Typography>
                  )}
                </FormControl>
                <FormControl
                  disabled={!editable}
                  error={Boolean(validationErrors.priorConsultationAssessment)}
                  fullWidth
                  margin='normal'
                  variant='outlined'
                >
                  <InputLabel>{t(messages.sections.overall.prior_consultation)}</InputLabel>
                  <Select
                    label={t(messages.sections.overall.prior_consultation)}
                    onChange={event =>
                      patchFormValues({
                        priorConsultationAssessment: event.target.value as typeof formValues.priorConsultationAssessment,
                      })
                    }
                    value={formValues.priorConsultationAssessment}
                  >
                    {DPIA_PRIOR_CONSULTATION_OPTIONS.map(option => (
                      <MenuItem key={option} value={option}>
                        {t(messages.prior_consultation[option])}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.priorConsultationAssessment && (
                    <Typography color='error' variant='caption'>
                      {validationErrors.priorConsultationAssessment}
                    </Typography>
                  )}
                </FormControl>
              </div>
              <TextField
                disabled={!editable}
                error={Boolean(validationErrors.ownerConclusion)}
                fullWidth
                helperText={validationErrors.ownerConclusion}
                label={t(messages.sections.overall.owner_conclusion)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ ownerConclusion: event.target.value })}
                rows={4}
                value={formValues.ownerConclusion}
                variant='outlined'
              />
            </Box>

            {(detail.reviewDecisionComment || detail.approvedAt || detail.decision) && (
              <Box className={styles.apaSection}>
                <div className={styles.reviewEyebrow}>{t(messages.sections.decision.eyebrow)}</div>
                <Typography gutterBottom variant='h3'>
                  {t(messages.sections.decision.title)}
                </Typography>
                {detail.decision && (
                  <Typography variant='body2'>
                    <strong>{t(messages.sections.decision.decision_outcome)}:</strong> {getDpiaDecisionLabel(detail.decision, t)}
                  </Typography>
                )}
                {detail.approvedAt && (
                  <Typography variant='body2'>
                    <strong>{t(messages.sections.decision.decision_date)}:</strong> {detail.approvedAt.slice(0, 10)}
                    {detail.approvedByName ? ` · ${detail.approvedByName}` : ''}
                  </Typography>
                )}
                {detail.reviewDecisionComment && (
                  <Box className={styles.reviewBanner}>
                    <Typography color='textSecondary' variant='caption'>
                      {t(messages.sections.decision.review_comment)}
                    </Typography>
                    <Typography variant='body2'>{detail.reviewDecisionComment}</Typography>
                  </Box>
                )}
              </Box>
            )}

            {showClarificationSection && (
              <Box className={styles.apaSection}>
                <div className={styles.reviewEyebrow}>SECTION 5</div>
                <Typography gutterBottom variant='h3'>
                  {t(messages.sections.clarification.title)}
                </Typography>
                {hasClarificationThread ? (
                  <DpiaReviewHistoryList entries={detail.clarificationHistory} />
                ) : (
                  detail.administratorClarificationComment && (
                    <Box className={styles.reviewBanner}>
                      <Typography variant='body2'>{detail.administratorClarificationComment}</Typography>
                    </Box>
                  )
                )}
                <TextField
                  disabled={!editable}
                  error={Boolean(validationErrors.ownerResponseComment)}
                  fullWidth
                  helperText={validationErrors.ownerResponseComment}
                  label={t(messages.sections.clarification.response)}
                  margin='normal'
                  multiline
                  onChange={event => patchFormValues({ ownerResponseComment: event.target.value })}
                  rows={4}
                  value={formValues.ownerResponseComment}
                  variant='outlined'
                />
              </Box>
            )}

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 6</div>
              <Typography className={styles.apaSectionTitle} gutterBottom variant='h3'>
                {t(messages.sections.completeness.title)}
              </Typography>
              <div className={styles.completenessList}>
                {completenessItems.map(item => (
                  <div
                    key={item.id}
                    className={`${styles.completenessItem} ${
                      item.complete ? styles.completenessItemComplete : styles.completenessItemIncomplete
                    }`}
                  >
                    <span className={styles.completenessItemIcon}>{item.complete ? '✓' : '!'}</span>
                    <Typography component='span' variant='body2'>
                      {t(messages.completeness[item.id as keyof typeof messages.completeness])}
                    </Typography>
                  </div>
                ))}
              </div>
            </Box>

            {editable && (
              <Box className={`${styles.apaSection} ${styles.adminDpiaReviewDecisionSection}`}>
                <div className={styles.apaSectionHeader}>
                  <Typography className={styles.apaSectionTitle} variant='h3'>
                    {t(messages.sections.submit.title)}
                  </Typography>
                  <Typography className={styles.apaSectionHelp} variant='body2'>
                    {t(messages.sections.submit.help)}
                  </Typography>
                </div>
                <div className={styles.apaSectionContent}>
                  <Typography className={styles.apaSectionNote} variant='body2'>
                    {t(messages.sections.submit.notice)}
                  </Typography>
                  <Box className={styles.formActions}>
                    <ColoredButton
                      className=''
                      customColor='secondary'
                      disabled={isSubmitting || isSaving}
                      icon={false}
                      onClick={handleSubmitClick}
                      showLoader={isSubmitting}
                      textLike={false}
                      variant='contained'
                    >
                      {t(messages.submit)}
                    </ColoredButton>
                  </Box>
                </div>
              </Box>
            )}

            {editable && (
              <Box className={`${styles.formActions} ${styles.formActionsSectionFooter}`}>
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled={isSaving || isSubmitting}
                  icon={false}
                  onClick={() => handleSaveClick(false)}
                  showLoader={isSaving}
                  textLike={false}
                  variant='outlined'
                >
                  {t(messages.save_and_continue)}
                </ColoredButton>
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled={isSaving || isSubmitting}
                  icon={false}
                  onClick={() => handleSaveClick(true)}
                  showLoader={isSaving}
                  textLike={false}
                  variant='outlined'
                >
                  {t(messages.save_and_close)}
                </ColoredButton>
              </Box>
            )}

            {canReassess && (
              <Box className={styles.closureBanner}>
                <Typography gutterBottom variant='h4'>
                  {t(messages.start_reassessment)}
                </Typography>
                <div className={styles.pageActions}>
                  <ColoredButton
                    className=''
                    customColor='secondary'
                    disabled={reassessMutation.isLoading}
                    icon={false}
                    onClick={() => setReassessOpen(true)}
                    showLoader={reassessMutation.isLoading}
                    textLike={false}
                    variant='contained'
                  >
                    {t(messages.start_reassessment)}
                  </ColoredButton>
                </div>
              </Box>
            )}
          </section>
        </div>
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <NavigationPrompt />
        <ManagePageHeader backButton noBorder onBack={() => handleNavigation()} subtitle={t(messages.page_subtitle)}>
          {detail?.title ?? t(messages.page_title)}
        </ManagePageHeader>
        {content}
        <DpiaReassessDialog
          loading={reassessMutation.isLoading}
          onClose={() => setReassessOpen(false)}
          onConfirm={handleReassessConfirm}
          open={reassessOpen}
        />
      </Container>
    </ErrorBoundary>
  );
};

export default OwnerDpiaEditorPage;
